// Заготовки: шаблоны поездок и частые блоки.
//
// Чего здесь намеренно нет. Шаблон не выдумывает ни рейсов, ни цен. Рейс у
// каждого свой — его вставляют ссылкой или заводят руками, а подставленный
// «пример» пришлось бы сначала удалять. Цена же, взятая с потолка, молча уехала
// бы в итог и наврала бы в сравнении вариантов. Поэтому в заготовке лежит ровно
// то, что повторяется из поездки в поездку: набор расходных блоков в разумном
// порядке, с разумным числом ночей и дней и с нулевой ценой — пустая сумма
// честно видна, а не притворяется расчётом.
//
// Кратность выбрана так, чтобы умножение на путешественников не врало: такси,
// еду и «прочее» ядро умножает на travelers (PER_TRAVELER_KINDS), поэтому в
// «прочее» попадают только те расходы, что и правда считаются на человека —
// страховка, связь, экскурсии, виза, подъёмник. Общий на всех переезд из
// аэропорта — transfer с perPerson: false, он на людей не множится.
import type { AppState, Currency, Segment, TripVariant } from '../types';
import { MAX_VARIANTS } from '../types';
import type { Translate } from '../i18n';
import { VARIANT_COLORS, emptyState, newId } from '../store';

/** Типы блоков заготовок. Рейс и багаж сюда не входят: см. шапку файла. */
export type BlockKind = 'transfer' | 'lodging' | 'taxi' | 'food' | 'misc';

export interface BlockSpec {
  /** Стабильный ключ блока внутри списка — им же различаются чипсы в разметке. */
  id: string;
  kind: BlockKind;
  /**
   * Ключ подписи: название расхода, город для жилья, «куда» для переезда.
   * Текст резолвится в момент добавления — в языке, на котором человек работает.
   */
  labelKey: string;
  /** Переезд: «откуда». */
  fromKey?: string;
  /** Ночи для жилья, дни/поездки для расходов. Всегда >= 1. */
  count: number;
  /** Переезд: время в пути, минуты. */
  durationMin?: number;
}

export interface TripTemplate {
  id: string;
  nameKey: string;
  descKey: string;
  /** Порядок блоков — это и порядок на таймлайне варианта. */
  blocks: readonly BlockSpec[];
}

// ── Частые блоки ──────────────────────────────────────────────────────────
// Они же кирпичи шаблонов: количество в шаблоне переопределяется спредом,
// подписи и смысл остаются общими.

const TRANSFER_AIRPORT: BlockSpec = {
  id: 'transfer.airport',
  kind: 'transfer',
  fromKey: 'tpl.place.airport',
  labelKey: 'tpl.place.city',
  count: 1,
  durationMin: 60,
};

const HOTEL: BlockSpec = {
  id: 'lodging.hotel',
  kind: 'lodging',
  labelKey: 'tpl.place.city',
  count: 2,
};

const TAXI: BlockSpec = { id: 'taxi.city', kind: 'taxi', labelKey: 'tpl.block.taxi', count: 2 };

const FOOD: BlockSpec = { id: 'food.days', kind: 'food', labelKey: 'tpl.block.food', count: 3 };

const INSURANCE: BlockSpec = {
  id: 'misc.insurance',
  kind: 'misc',
  labelKey: 'tpl.block.insurance',
  count: 1,
};

const ESIM: BlockSpec = { id: 'misc.esim', kind: 'misc', labelKey: 'tpl.block.esim', count: 1 };

const TICKETS: BlockSpec = {
  id: 'misc.tickets',
  kind: 'misc',
  labelKey: 'tpl.block.tickets',
  count: 1,
};

const VISA: BlockSpec = { id: 'misc.visa', kind: 'misc', labelKey: 'tpl.block.visa', count: 1 };

/** Ряд «частых блоков» в библиотеке: восемь расходов, которые заводят чаще всего. */
export const BLOCK_PRESETS: readonly BlockSpec[] = [
  TRANSFER_AIRPORT,
  HOTEL,
  TAXI,
  FOOD,
  INSURANCE,
  ESIM,
  TICKETS,
  VISA,
];

// ── Шаблоны поездок ───────────────────────────────────────────────────────

export const TRIP_TEMPLATES: readonly TripTemplate[] = [
  {
    id: 'weekend',
    nameKey: 'tpl.weekend.name',
    descKey: 'tpl.weekend.desc',
    blocks: [
      TRANSFER_AIRPORT,
      { ...HOTEL, count: 2 },
      { ...FOOD, count: 3 },
      { ...TAXI, count: 2 },
      INSURANCE,
    ],
  },
  {
    id: 'beach',
    nameKey: 'tpl.beach.name',
    descKey: 'tpl.beach.desc',
    blocks: [
      { ...TRANSFER_AIRPORT, labelKey: 'tpl.place.hotel', durationMin: 45 },
      { ...HOTEL, count: 7 },
      { ...FOOD, count: 7 },
      TICKETS,
      ESIM,
      INSURANCE,
    ],
  },
  {
    id: 'business',
    nameKey: 'tpl.business.name',
    descKey: 'tpl.business.desc',
    blocks: [
      TRANSFER_AIRPORT,
      { ...HOTEL, count: 3 },
      { ...TAXI, count: 4 },
      { ...FOOD, count: 4 },
      ESIM,
      INSURANCE,
    ],
  },
  {
    id: 'two-cities',
    nameKey: 'tpl.twoCities.name',
    descKey: 'tpl.twoCities.desc',
    blocks: [
      { ...HOTEL, id: 'lodging.city1', labelKey: 'tpl.place.city1', count: 3 },
      {
        ...TRANSFER_AIRPORT,
        id: 'transfer.cities',
        fromKey: 'tpl.place.city1',
        labelKey: 'tpl.place.city2',
        durationMin: 240,
      },
      { ...HOTEL, id: 'lodging.city2', labelKey: 'tpl.place.city2', count: 3 },
      { ...FOOD, count: 7 },
      { ...TAXI, count: 4 },
      INSURANCE,
    ],
  },
  {
    id: 'mountains',
    nameKey: 'tpl.mountains.name',
    descKey: 'tpl.mountains.desc',
    blocks: [
      TRANSFER_AIRPORT,
      { ...HOTEL, count: 5 },
      { id: 'misc.skipass', kind: 'misc', labelKey: 'tpl.block.skipass', count: 5 },
      { id: 'misc.gear', kind: 'misc', labelKey: 'tpl.block.gear', count: 5 },
      { ...FOOD, count: 6 },
      INSURANCE,
    ],
  },
];

// ── Сборка ────────────────────────────────────────────────────────────────

/** Подпись блока для чипса: «Аэропорт → Город», «Город», «Еда». */
export function blockLabel(spec: BlockSpec, t: Translate): string {
  const label = t(spec.labelKey);
  return spec.fromKey ? `${t(spec.fromKey)} → ${label}` : label;
}

/**
 * Кратность блока для чипса. У жилья это ночи — их называем словом, иначе «×7»
 * читается как семь отелей. У остальных множитель понятен и без слов, а при
 * единице его и вовсе не показываем.
 */
export function blockCountLabel(spec: BlockSpec, t: Translate): string {
  if (spec.kind === 'lodging') return t('summary.nights', { count: spec.count });
  return spec.count > 1 ? `×${spec.count}` : '';
}

/**
 * Сегмент по заготовке. Цена нулевая и в валюте отображения — так сумма сразу
 * попадает в нужную колонку сравнения, а не тянет за собой конвертацию.
 */
export function buildBlock(spec: BlockSpec, t: Translate, currency: Currency): Segment {
  const price = { amount: 0, currency };
  switch (spec.kind) {
    case 'transfer':
      return {
        kind: 'transfer',
        id: newId('t'),
        from: spec.fromKey ? t(spec.fromKey) : '',
        to: t(spec.labelKey),
        durationMin: spec.durationMin ?? 0,
        price,
        perPerson: false,
      };
    case 'lodging':
      return {
        kind: 'lodging',
        id: newId('l'),
        city: t(spec.labelKey),
        nights: Math.max(1, spec.count),
        pricePerNight: price,
        perPerson: false,
      };
    default:
      return {
        kind: spec.kind,
        id: newId('c'),
        label: t(spec.labelKey),
        price,
        quantity: Math.max(1, spec.count),
      };
  }
}

/**
 * Шаблон, влитый в состояние отдельным вариантом. Библиотека только пополняется:
 * то, что человек уже собрал, шаблон не трогает — иначе одно нажатие стирало бы
 * работу, а отмены в приложении нет.
 *
 * На пределе вариантов возвращает состояние как есть: вставить некуда.
 */
export function withTemplate(state: AppState, tpl: TripTemplate, t: Translate): AppState {
  if (state.variants.length >= MAX_VARIANTS) return state;

  const segments = tpl.blocks.map((spec) => buildBlock(spec, t, state.displayCurrency));
  const library = { ...state.library };
  for (const seg of segments) library[seg.id] = seg;

  const variant: TripVariant = {
    id: newId('v'),
    name: t('variant.defaultName', { n: state.variants.length + 1 }),
    color: VARIANT_COLORS[state.variants.length % VARIANT_COLORS.length] ?? '#5b7c99',
    segmentIds: segments.map((s) => s.id),
  };

  return { ...state, library, variants: [...state.variants, variant] };
}

/** Состояние новой поездки, заведённой из шаблона. */
export function templateState(tpl: TripTemplate, t: Translate): AppState {
  return withTemplate(emptyState(), tpl, t);
}
